import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Message, User } from '../../types';
import MessageItem from './MessageItem';

const MessageListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const LoadMoreButton = styled.button`
  align-self: center;
  padding: 0.5rem 1rem;
  margin: 1rem 0;
  background: white;
  border: 1px solid #e1e5e9;
  border-radius: 20px;
  color: #667eea;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9ff;
    border-color: #667eea;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DateSeparator = styled.div`
  display: flex;
  align-items: center;
  margin: 1rem 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e1e5e9;
  }
  
  span {
    padding: 0 1rem;
    font-size: 0.8rem;
    color: #666;
    background: white;
    white-space: nowrap;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #666;
  text-align: center;
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.3;
  }
  
  h3 {
    margin-bottom: 0.5rem;
    color: #333;
  }
  
  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

interface MessageListProps {
  messages: Message[];
  currentUser: User;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  currentUser,
  onLoadMore,
  hasMore,
  isLoading
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeight = useRef<number>(0);

  // Maintain scroll position when loading more messages
  useEffect(() => {
    const container = containerRef.current;
    if (container && hasMore && messages.length > 0) {
      const newScrollHeight = container.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeight.current;
      container.scrollTop += scrollDiff;
      prevScrollHeight.current = newScrollHeight;
    }
  }, [messages, hasMore]);

  // Update scroll height reference
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      prevScrollHeight.current = container.scrollHeight;
    }
  }, []);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const shouldShowDateSeparator = (currentMessage: Message, previousMessage?: Message): boolean => {
    if (!previousMessage) return true;
    
    const currentDate = new Date(currentMessage.timestamp).toDateString();
    const previousDate = new Date(previousMessage.timestamp).toDateString();
    
    return currentDate !== previousDate;
  };

  const shouldGroupMessages = (currentMessage: Message, previousMessage?: Message): boolean => {
    if (!previousMessage) return false;
    
    // Don't group if different users
    if (currentMessage.userId !== previousMessage.userId) return false;
    
    // Don't group if more than 5 minutes apart
    const currentTime = new Date(currentMessage.timestamp).getTime();
    const previousTime = new Date(previousMessage.timestamp).getTime();
    const timeDiff = (currentTime - previousTime) / (1000 * 60); // in minutes
    
    return timeDiff <= 5;
  };

  if (messages.length === 0 && !isLoading) {
    return (
      <MessageListContainer ref={containerRef}>
        <EmptyState>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20,2H4A2,2 0 0,0 2,4V22L6,18H20A2,2 0 0,0 22,16V4C22,2.89 21.1,2 20,2Z"/>
          </svg>
          <h3>No messages yet</h3>
          <p>Start the conversation by sending a message!</p>
        </EmptyState>
      </MessageListContainer>
    );
  }

  return (
    <MessageListContainer ref={containerRef}>
      {hasMore && !isLoading && (
        <LoadMoreButton onClick={onLoadMore}>
          Load more messages
        </LoadMoreButton>
      )}
      
      {isLoading && hasMore && (
        <LoadMoreButton disabled>
          Loading...
        </LoadMoreButton>
      )}

      {messages.map((message, index) => {
        const previousMessage = index > 0 ? messages[index - 1] : undefined;
        const showDateSeparator = shouldShowDateSeparator(message, previousMessage);
        const isGrouped = shouldGroupMessages(message, previousMessage);
        const isOwn = message.userId === currentUser.id;

        return (
          <React.Fragment key={message.id}>
            {showDateSeparator && (
              <DateSeparator>
                <span>{formatDate(message.timestamp)}</span>
              </DateSeparator>
            )}
            
            <MessageItem
              message={message}
              isOwn={isOwn}
              isGrouped={isGrouped}
              showAvatar={!isGrouped || message.isAiResponse}
            />
          </React.Fragment>
        );
      })}
    </MessageListContainer>
  );
};

export default MessageList; 